import { DealsContainer } from 'containers/deals';
import { useFBTrackSearch } from 'hooks/useFBTrackSearch';
import { useSyncSearchFilters } from 'hooks/useSyncSearchFilters';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { DefaultContainerProps, DefaultPageProps } from 'pages';
import { FC } from 'react';
import { cleanObject, getFiltersFromUrl } from 'services/filters/utils';
import Rest from 'services/rest';
import { getStateByZip } from 'shared/services/geo';
import {
  AuctionFromSearch,
  DynamicAuctionFilters,
  GetAuctionsRequest,
  GetAuctionsResponse,
  SearchFilters,
} from 'shared/types/api/getAuctions';
import { FiltersState } from 'shared/types/common';
import {
  AUCTIONS_PER_PAGE,
  MAX_FEATURED_AUCTIONS,
} from 'shared/utils/constants';
import authorized from 'utils/authorized';
import { getDeviceType, UserAgentDeviceType } from 'utils/functions';
import { SEO_SEARCH_PAGE } from 'utils/seo';

type DealsPageProps = DefaultPageProps<{
  filtersAtomData: FiltersState;
  dynamicFilters: DynamicAuctionFilters;
  searchFilters: SearchFilters;
  auctions: AuctionFromSearch[];
  featuredAuctions: AuctionFromSearch[];
  auctionsCount: number;
  zipcode: string;
  perPage: number;
  isMobileView: boolean;
}>;

// all page props excluding user
export type DealsContainerProps = DefaultContainerProps<DealsPageProps>;

function getQueryString(params: Record<string, string | number>) {
  const query = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  return query ? `?${query}` : '';
}

const DealsPage: FC<DealsPageProps> = (props) => {
  useFBTrackSearch();
  useSyncSearchFilters(props.filtersAtomData);

  const { query, pathname } = useRouter();

  function getCanonicalLink(): string {
    if (query.make && !query.model) {
      return `${process.env.PUBLIC_SITE_URL}/makes/${query.make}`;
    }
    if (query.zipcode) {
      return `${process.env.PUBLIC_SITE_URL}/state/${getStateByZip(
        typeof query.zipcode == 'string' ? query.zipcode : query.zipcode[0],
      ).fullState.replace(/\s/g, '')}`;
    }
    return `${process.env.PUBLIC_SITE_URL}/deals${canonicalParamsPath}`;
  }

  const makePath = query.make ? `make=${query.make}` : '';
  const modelPath = query.model ? `model=${query.model}` : '';
  const paramsPath = [makePath, modelPath].filter(Boolean).join('&');
  const canonicalParamsPath = paramsPath ? `?${paramsPath}` : '';
  const canonicalHref = getCanonicalLink();

  const perPage = props.perPage || Number(query.perPage || '0');
  const offset = Number(query.offset || '0');

  const isNextPage = offset + perPage < props.auctionsCount;
  const nextQuery = {
    ...query,
    offset: offset + perPage,
  };
  const isPrevPage = offset > 0;
  const prevQuery = {
    ...query,
    offset: offset - perPage,
  };

  if (prevQuery.offset <= 0) {
    delete prevQuery.offset;
  }

  return (
    <>
      <Head>
        <title>{SEO_SEARCH_PAGE.title(props.zipcode, query)}</title>
        <meta
          name='description'
          content={SEO_SEARCH_PAGE.description(props.zipcode, query)}
        />
        <meta
          name='keywords'
          content={SEO_SEARCH_PAGE.keywords(props.zipcode, query)}
        />
        <link rel='canonical' href={canonicalHref} />
        {isPrevPage && (
          <link
            rel='prev'
            href={
              process.env.PUBLIC_SITE_URL + pathname + getQueryString(prevQuery)
            }
          />
        )}
        {isNextPage && (
          <link
            rel='next'
            href={
              process.env.PUBLIC_SITE_URL + pathname + getQueryString(nextQuery)
            }
          />
        )}
      </Head>
      <DealsContainer {...props} />
    </>
  );
};

const GENERIC_EMPTY_AUCTIONS_RESPONSE = {
  perPage: AUCTIONS_PER_PAGE,
  auctions: [],
  count: 0,
  dynamicFilters: {},
  searchFilters: {
    makes: [],
    models: [],
    years: [],
    trims: [],
  },
};

const getAuctions = async (rest: Rest, params: GetAuctionsRequest) => {
  try {
    const result = await rest.getAuctions(params);
    return result;
  } catch (e) {
    return GENERIC_EMPTY_AUCTIONS_RESPONSE;
  }
};
// alias
const getAuctionsFeatured = async (rest: Rest, params: GetAuctionsRequest) => {
  try {
    const result = await rest.getAuctionsFeatured(params);
    return result;
  } catch (e) {
    return GENERIC_EMPTY_AUCTIONS_RESPONSE;
  }
};

export const getServerSideProps: GetServerSideProps =
  authorized<DealsPageProps>(async ({ query, req }, rest, auth, user) => {
    const deviceType = getDeviceType(req.headers['user-agent']);
    const isMobileView = deviceType === UserAgentDeviceType.MOBILE;

    const filtersFromUrl = getFiltersFromUrl(query, true);

    const isZipcodeInQuery = 'zipcode' in query;
    if (!isZipcodeInQuery) {
      filtersFromUrl.zipcode = user?.zipcode || null;
    }
    const filters = cleanObject(filtersFromUrl);
    const getAuctionRequest: GetAuctionsRequest = filters;

    const auctionsPrommise = getAuctions(rest, getAuctionRequest);

    const featuredAuctionsPromise: Promise<
      GetAuctionsResponse | typeof GENERIC_EMPTY_AUCTIONS_RESPONSE
    > = filters.is_featured
      ? Promise.resolve(GENERIC_EMPTY_AUCTIONS_RESPONSE)
      : getAuctionsFeatured(rest, {
          ...filters,
          is_featured: true,
          completed: false,
          offset: 0,
          perPage: MAX_FEATURED_AUCTIONS,
        });

    const [auctions, featuredAuctions] = await Promise.all([
      auctionsPrommise,
      featuredAuctionsPromise,
    ]);

    return {
      props: {
        filtersAtomData: filters,
        dynamicFilters: auctions.dynamicFilters,
        searchFilters: auctions.searchFilters,
        auctions: auctions.auctions,
        featuredAuctions: featuredAuctions.auctions,
        auctionsCount: auctions.count,
        user,
        zipcode: filters.zipcode || '',
        perPage: auctions.perPage || AUCTIONS_PER_PAGE,
        isMobileView,
      },
    };
  });

export default DealsPage;
